
import { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

export default function SpecialsManagement({ theme }) {
  const { t, isSpanish } = useLanguage();
  const catName = (cat) => isSpanish ? (cat.name_español || cat.name) : cat.name;
  const [specials, setSpecials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [options, setOptions] = useState([]); // <-- options from backend
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [editingSpecial, setEditingSpecial] = useState(null);
  const [showAddSpecial, setShowAddSpecial] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [specialForm, setSpecialForm] = useState({
    name: '',
    description: '',
    description_español: '',
    active: 1,
    special_type: 'buy_x_get_y_free',
    conditions: {
      category_id: null,
      product_id: null,
      buy_quantity: 2,
      get_quantity: 1,
      applies_to: 'cheapest',
      min_quantity: 1,
      combo_items: []
    },
    discount_value: null,
    combo_price: null,
    start_date: '',
    end_date: '',
    start_time: '00:00',
    end_time: '23:59',
    days_of_week: [],
    priority: 0
  });

  useEffect(() => {
    fetchSpecials();
    fetchCategories();
    fetchProducts();
    fetchOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!showAddSpecial && !editingSpecial) return;

    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        setShowAddSpecial(false);
        setEditingSpecial(null);
        resetForm();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [showAddSpecial, editingSpecial]);

  const fetchOptions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/debug/options`);
      if (response.ok) {
        const data = await response.json();
        setOptions(data);
      } else {
        setOptions([]);
      }
    } catch (err) {
      setOptions([]);
    }
  };

  const fetchSpecials = async () => {
    setLoading(true);
    setError(null);

    try {
      const url = showInactive
        ? `${BACKEND_URL}/api/specials?show_inactive=true`
        : `${BACKEND_URL}/api/specials`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(t('failedToFetchSpecials'));
      }

      const data = await response.json();
      setSpecials(data);
    } catch (err) {
      console.error('Error fetching specials:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const handleSaveSpecial = async () => {
    try {
      const method = editingSpecial ? 'PUT' : 'POST';
      const url = editingSpecial
        ? `${BACKEND_URL}/api/specials/${editingSpecial.id}`
        : `${BACKEND_URL}/api/specials`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(specialForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('failedToSaveSpecial'));
      }

      alert(editingSpecial ? t('specialUpdated') : t('specialCreated'));
      setShowAddSpecial(false);
      setEditingSpecial(null);
      resetForm();
      fetchSpecials();
    } catch (err) {
      console.error('Error saving special:', err);
      alert(t('error') + ': ' + err.message);
    }
  };

  const handleDeleteSpecial = async (specialId) => {
    if (!window.confirm(t('confirmDeleteSpecial'))) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/specials/${specialId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(t('failedToDeleteSpecial'));
      }

      alert(t('specialDeleted'));
      fetchSpecials();
    } catch (err) {
      console.error('Error deleting special:', err);
      alert(t('error') + ': ' + err.message);
    }
  };

  const handleOpenEditSpecial = (special) => {
    // Normalize category_ids for edit mode
    let cond = { ...special.conditions };
    if (!Array.isArray(cond.category_ids)) {
      if (cond.category_id != null) {
        cond.category_ids = [cond.category_id];
      } else {
        cond.category_ids = [];
      }
    }
    setSpecialForm({
      name: special.name,
      description: special.description || '',
      description_español: special.description_español || '',
      active: special.active,
      special_type: special.special_type,
      conditions: cond,
      discount_value: special.discount_value,
      combo_price: special.combo_price ?? null,
      start_date: special.start_date || '',
      end_date: special.end_date || '',
      start_time: special.start_time || '00:00',
      end_time: special.end_time || '23:59',
      days_of_week: special.days_of_week || [],
      priority: special.priority
    });
    setEditingSpecial(special);
  };

  const resetForm = () => {
    setSpecialForm({
      name: '',
      description: '',
      description_español: '',
      active: 1,
      special_type: 'buy_x_get_y_free',
      conditions: {
        category_id: null,
        category_ids: [],
        product_id: null,
        buy_quantity: 2,
        get_quantity: 1,
        applies_to: 'cheapest',
        min_quantity: 1,
        combo_items: [],
        exclude_product_ids: []
      },
      discount_value: null,
      combo_price: null,
      start_date: '',
      end_date: '',
      start_time: '00:00',
      end_time: '23:59',
      days_of_week: [],
      priority: 0
    });
  };

  const toggleDayOfWeek = (day) => {
    const currentDays = specialForm.days_of_week || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    setSpecialForm({ ...specialForm, days_of_week: newDays });
  };

  const updateCondition = (key, value) => {
    setSpecialForm({
      ...specialForm,
      conditions: {
        ...specialForm.conditions,
        [key]: value
      }
    });
  };

  const getSpecialTypeLabel = (type) => {
    switch (type) {
      case 'buy_x_get_y_free':
        return t('buyXGetYFree');
      case 'percentage_discount':
        return t('percentageDiscount');
      case 'fixed_discount':
        return t('fixedDiscount');
      case 'combo_deal':
        return t('comboDeal');
      default:
        return type;
    }
  };

  const getDayName = (dayNum) => {
    const days = [
      t('sunday'),
      t('monday'),
      t('tuesday'),
      t('wednesday'),
      t('thursday'),
      t('friday'),
      t('saturday')
    ];
    return days[dayNum];
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: theme?.colors?.textSecondary || '#666' }}>
        {t('loadingSpecials')}...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
        {t('error')}: {error}
      </div>
    );
  }

  // Filter specials based on search term
  const matchesSearch = (special) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();

    // Format date for searching
    const formatDate = (dateString) => {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString();
    };

    return [
      special.name,
      special.description,
      special.description_español,
      special.special_type,
      special.day_of_week,
      formatDate(special.start_date),
      formatDate(special.end_date),
      special.start_time,
      special.end_time,
      special.active ? t('active') : t('inactive')
    ].some(field =>
      field && field.toString().toLowerCase().includes(search)
    );
  };

  const filteredSpecials = specials.filter(matchesSearch);

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <label style={{
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.9rem',
            color: theme?.colors?.text || '#000',
            cursor: 'pointer'
          }}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              style={{ marginRight: '0.5rem' }}
            />
            {t('showInactiveSpecials')}
          </label>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowAddSpecial(true);
          }}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: theme?.colors?.primary || '#1976d2',
            color: theme?.colors?.surface || '#fff',
            border: 'none',
            borderRadius: theme?.borderRadius || 4,
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: 'bold'
          }}
        >
          {t('addSpecial')}
        </button>
      </div>

      {/* Search Input */}
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('searchSpecials')}
          style={{
            width: '100%',
            padding: '0.75rem',
            fontSize: '0.95rem',
            border: `1px solid ${theme?.colors?.border || '#ddd'}`,
            borderRadius: theme?.borderRadius || 4,
            backgroundColor: theme?.colors?.surface || '#fff',
            color: theme?.colors?.text || '#000',
            outline: 'none'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = theme?.colors?.primary || '#1976d2';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = theme?.colors?.border || '#ddd';
          }}
        />
        {searchTerm && (
          <div style={{
            marginTop: '0.5rem',
            fontSize: '0.85rem',
            color: theme?.colors?.textSecondary || '#666'
          }}>
            {t('showing')} {filteredSpecials.length} {t('of')} {specials.length} {t('specials').toLowerCase()}
          </div>
        )}
      </div>

      {/* Specials List */}
      {filteredSpecials.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          color: theme?.colors?.textSecondary || '#666',
          fontSize: '1.1rem'
        }}>
          {t('noSpecialsFound')}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))'
        }}>
          {filteredSpecials.map(special => (
            <div
              key={special.id}
              style={{
                backgroundColor: special.active
                  ? theme?.colors?.surface || '#fff'
                  : theme?.colors?.background || '#f5f5f5',
                border: `2px solid ${special.active ? theme?.colors?.primary || '#1976d2' : '#ccc'}`,
                borderRadius: theme?.borderRadius || 8,
                padding: '1rem',
                opacity: special.active ? 1 : 0.6
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '0.75rem'
              }}>
                <h3 style={{
                  margin: 0,
                  color: theme?.colors?.primary || '#1976d2',
                  fontSize: '1.2rem'
                }}>
                  {special.name}
                  {!special.active && (
                    <span style={{
                      marginLeft: '0.5rem',
                      fontSize: '0.8rem',
                      color: theme?.colors?.textSecondary || '#666'
                    }}>
                      ({t('inactive')})
                    </span>
                  )}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleOpenEditSpecial(special)}
                    style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: theme?.colors?.secondary || '#666',
                      color: theme?.colors?.surface || '#fff',
                      border: 'none',
                      borderRadius: theme?.borderRadius || 4,
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {t('edit')}
                  </button>
                  <button
                    onClick={() => handleDeleteSpecial(special.id)}
                    style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: '#d32f2f',
                      color: '#fff',
                      border: 'none',
                      borderRadius: theme?.borderRadius || 4,
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {t('delete')}
                  </button>
                </div>
              </div>

              {special.description && (
                <p style={{
                  margin: '0 0 0.75rem 0',
                  color: theme?.colors?.text || '#000',
                  fontSize: '0.9rem'
                }}>
                  {special.description}
                </p>
              )}

              <div style={{
                fontSize: '0.85rem',
                color: theme?.colors?.textSecondary || '#666',
                lineHeight: '1.6'
              }}>
                <div><strong>{t('specialType')}:</strong> {getSpecialTypeLabel(special.special_type)}</div>

                {special.days_of_week && special.days_of_week.length > 0 && (
                  <div>
                    <strong>{t('daysOfWeek')}:</strong> {special.days_of_week.map(d => getDayName(d)).join(', ')}
                  </div>
                )}

                {special.start_time && special.end_time && (
                  <div><strong>{t('timeRange')}:</strong> {special.start_time} - {special.end_time}</div>
                )}

                {special.start_date && special.end_date && (
                  <div><strong>{t('dateRange')}:</strong> {special.start_date} to {special.end_date}</div>
                )}

                {special.discount_value && (
                  <div><strong>{t('discountValue')}:</strong> {special.discount_value}%</div>
                )}

                <div><strong>{t('priority')}:</strong> {special.priority}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Special Modal */}
      {(showAddSpecial || editingSpecial) && (
        <div
          onMouseDown={(e) => {
            // Only close if clicking directly on the backdrop, not on child elements
            if (e.target === e.currentTarget) {
              setShowAddSpecial(false);
              setEditingSpecial(null);
              resetForm();
            }
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme?.colors?.surface || '#fff',
              borderRadius: theme?.borderRadius || 8,
              padding: '2rem',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
            <h3 style={{ color: theme?.colors?.primary || '#1976d2', marginTop: 0 }}>
              {editingSpecial ? t('editSpecial') : t('addNewSpecial')}
            </h3>

            {/* Top Button Section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
              <button
                onClick={() => {
                  setShowAddSpecial(false);
                  setEditingSpecial(null);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: theme?.colors?.error || '#f44336',
                  color: theme?.colors?.surface || '#fff',
                  border: 'none',
                  borderRadius: theme?.borderRadius || 4,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSaveSpecial}
                disabled={!specialForm.name}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: !specialForm.name ? '#ccc' : theme?.colors?.success || '#4caf50',
                  color: theme?.colors?.surface || '#fff',
                  border: 'none',
                  borderRadius: theme?.borderRadius || 4,
                  cursor: !specialForm.name ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  opacity: !specialForm.name ? 0.6 : 1
                }}
              >
                {t('save')}
              </button>
            </div>

            {/* Special Name */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('specialName')}:
              </label>
              <input
                type="text"
                value={specialForm.name}
                onChange={(e) => setSpecialForm({ ...specialForm, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Description */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('specialDescription')} ({t('english')}):
              </label>
              <textarea
                value={specialForm.description}
                onChange={(e) => setSpecialForm({ ...specialForm, description: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: '1px solid #ddd',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Description (Spanish) */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('specialDescription')} ({t('spanish')}):
              </label>
              <textarea
                value={specialForm.description_español}
                onChange={(e) => setSpecialForm({ ...specialForm, description_español: e.target.value })}
                rows={3}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: '1px solid #ddd',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            {/* Special Type */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('specialType')}:
              </label>
              <select
                value={specialForm.special_type}
                onChange={(e) => setSpecialForm({ ...specialForm, special_type: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              >
                <option value="buy_x_get_y_free">{t('buyXGetYFree')}</option>
                <option value="percentage_discount">{t('percentageDiscount')}</option>
                <option value="fixed_discount">{t('fixedDiscount')}</option>
                <option value="combo_deal">{t('comboDeal')}</option>
              </select>
            </div>

            {/* Category Selection - Multiple */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('selectCategory')} ({t('multipleAllowed')}):
              </label>
              <div style={{
                border: '1px solid #ddd',
                borderRadius: theme?.borderRadius || 4,
                padding: '0.5rem',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {categories.map(cat => (
                  <label key={cat.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.25rem',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={(specialForm.conditions.category_ids || []).includes(cat.id)}
                      onChange={(e) => {
                        const currentCats = specialForm.conditions.category_ids || [];
                        const newCats = e.target.checked
                          ? [...currentCats, cat.id]
                          : currentCats.filter(id => id !== cat.id);
                        setSpecialForm(form => ({
                          ...form,
                          conditions: {
                            ...form.conditions,
                            category_ids: newCats,
                            category_id: null // always clear single when using multiple
                          }
                        }));
                      }}
                      style={{ marginRight: '0.5rem' }}
                    />
                    {catName(cat)}
                  </label>
                ))}
              </div>
            </div>

            {/* Product Exclusions */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('excludeProducts')} ({t('optional')}):
              </label>
              <select
                multiple
                size="5"
                value={specialForm.conditions.exclude_product_ids || []}
                onChange={(e) => {
                  e.stopPropagation();
                  const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                  updateCondition('exclude_product_ids', selected);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: '1px solid #ddd',
                  fontSize: '0.9rem'
                }}
              >
                {products
                  .filter(p => {
                    const selectedCats = specialForm.conditions.category_ids || [];
                    // Only show products in selected categories and active
                    return (selectedCats.length === 0 || selectedCats.includes(p.category_id)) && p.active !== 0;
                  })
                  .map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))
                }
              </select>
              <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666', marginTop: '0.25rem' }}>
                {t('holdCtrlToSelectMultiple')}
              </div>
            </div>

            {/* Option Exclusions */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('excludeOptions')} ({t('optional')}):
              </label>
              <select
                multiple
                size="5"
                value={specialForm.conditions.exclude_option_ids || []}
                onChange={(e) => {
                  e.stopPropagation();
                  const selected = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                  updateCondition('exclude_option_ids', selected);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: '1px solid #ddd',
                  fontSize: '0.9rem'
                }}
              >
                {(() => {
                  // Get all products in selected categories and active
                  const selectedCats = specialForm.conditions.category_ids || [];
                  const relevantProducts = products.filter(p => (selectedCats.length === 0 || selectedCats.includes(p.category_id)) && p.active !== 0);
                  // Collect all option_group ids from these products
                  const optionGroupIds = new Set();
                  const optionsSet = new Set();
                  relevantProducts.forEach(p => {
                    let groupId = null;
                    if (p.option_group != null && typeof p.option_group === 'object' && p.option_group.id != null) {
                      groupId = p.option_group.id;
                      if (Array.isArray(p.option_group.options)) {
                        p.option_group.options.forEach(opt => {
                          if (opt && typeof opt.id === 'number') {
                            optionsSet.add(opt.id);
                          }
                        });
                      }
                    } else if (typeof p.option_group === 'number') {
                      groupId = p.option_group;
                    }
                    if (typeof groupId === 'number') {
                      optionGroupIds.add(groupId);
                    }
                  });
                  // Correct filter: match option.option_group_id to optionGroupIds
                  const filteredOptions = options.filter(option => optionGroupIds.has(option.option_group_id));
                  return filteredOptions
                    .map(option => (
                      <option key={option.id} value={option.id}>
                        {option.name}
                      </option>
                    ));
                })()}
              </select>
              <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666', marginTop: '0.25rem' }}>
                {t('holdCtrlToSelectMultiple')}
              </div>
            </div>

            {/* Buy X Get Y Free Specific Fields */}
            {specialForm.special_type === 'buy_x_get_y_free' && (
              <>
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                      {t('buyQuantity')}:
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={specialForm.conditions.buy_quantity || 2}
                      onChange={(e) => updateCondition('buy_quantity', parseInt(e.target.value))}
                      onWheel={(e) => e.target.blur()}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: theme?.borderRadius || 4,
                        border: '1px solid #ddd',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                      {t('getQuantity')}:
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={specialForm.conditions.get_quantity || 1}
                      onChange={(e) => updateCondition('get_quantity', parseInt(e.target.value))}
                      onWheel={(e) => e.target.blur()}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: theme?.borderRadius || 4,
                        border: '1px solid #ddd',
                        fontSize: '1rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                    {t('appliesTo')}:
                  </label>
                  <select
                    value={specialForm.conditions.applies_to || 'cheapest'}
                    onChange={(e) => updateCondition('applies_to', e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: theme?.borderRadius || 4,
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  >
                    <option value="cheapest">{t('cheapest')}</option>
                    <option value="mostExpensive">{t('mostExpensive')}</option>
                    <option value="first">{t('first')}</option>
                    <option value="last">{t('last')}</option>
                  </select>
                </div>
              </>
            )}

            {/* Discount Value for percentage/fixed discounts */}
            {(specialForm.special_type === 'percentage_discount' || specialForm.special_type === 'fixed_discount') && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                  {t('discountValue')} {specialForm.special_type === 'percentage_discount' ? '(%)' : '($)'}:
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={specialForm.discount_value || ''}
                  onChange={(e) => setSpecialForm({ ...specialForm, discount_value: parseFloat(e.target.value) })}
                  onWheel={(e) => e.target.blur()}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    borderRadius: theme?.borderRadius || 4,
                    border: '1px solid #ddd',
                    fontSize: '1rem'
                  }}
                />
              </div>
            )}

            {/* Combo Deal Specific Fields */}
            {specialForm.special_type === 'combo_deal' && (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                    {t('comboPrice')} ($):
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={specialForm.combo_price || ''}
                    onChange={(e) => setSpecialForm({ ...specialForm, combo_price: parseFloat(e.target.value) })}
                    onWheel={(e) => e.target.blur()}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: theme?.borderRadius || 4,
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                    placeholder="25.00"
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                    Max Discount ($):
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={specialForm.discount_value || ''}
                    onChange={(e) => setSpecialForm({ ...specialForm, discount_value: parseFloat(e.target.value) })}
                    onWheel={(e) => e.target.blur()}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: theme?.borderRadius || 4,
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                    placeholder="59.00"
                  />
                  <div style={{ fontSize: '0.8rem', color: theme?.colors?.textSecondary || '#666', marginTop: '0.25rem' }}>
                    Leave blank for no limit (uses combo price only)
                  </div>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                    {t('comboItems')}:
                  </label>

                  {(specialForm.conditions.combo_items || []).map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                      alignItems: 'center'
                    }}>
                      <select
                        value={item.category_id || ''}
                        onChange={(e) => {
                          const newComboItems = [...specialForm.conditions.combo_items];
                          newComboItems[index].category_id = parseInt(e.target.value);
                          updateCondition('combo_items', newComboItems);
                        }}
                        style={{
                          flex: 2,
                          padding: '0.5rem',
                          borderRadius: theme?.borderRadius || 4,
                          border: '1px solid #ddd',
                          fontSize: '0.9rem'
                        }}
                      >
                        <option value="">{t('selectCategory')}</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{catName(cat)}</option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity || 1}
                        onChange={(e) => {
                          const newComboItems = [...specialForm.conditions.combo_items];
                          newComboItems[index].quantity = parseInt(e.target.value);
                          updateCondition('combo_items', newComboItems);
                        }}
                        onWheel={(e) => e.target.blur()}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          borderRadius: theme?.borderRadius || 4,
                          border: '1px solid #ddd',
                          fontSize: '0.9rem'
                        }}
                        placeholder="Qty"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newComboItems = specialForm.conditions.combo_items.filter((_, i) => i !== index);
                          updateCondition('combo_items', newComboItems);
                        }}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: '#f44336',
                          color: '#fff',
                          border: 'none',
                          borderRadius: theme?.borderRadius || 4,
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => {
                      const currentComboItems = Array.isArray(specialForm.conditions.combo_items)
                        ? specialForm.conditions.combo_items
                        : [];
                      const newComboItems = [
                        ...currentComboItems,
                        { category_id: null, quantity: 1 }
                      ];
                      updateCondition('combo_items', newComboItems);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: theme?.colors?.secondary || '#666',
                      color: theme?.colors?.surface || '#fff',
                      border: 'none',
                      borderRadius: theme?.borderRadius || 4,
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      width: '100%'
                    }}
                  >
                    + {t('addComboItem')}
                  </button>

                  <div style={{ fontSize: '0.75rem', color: theme?.colors?.textSecondary || '#666', marginTop: '0.5rem' }}>
                    {t('comboExample')}: 1 Pizza + 2 Drinks = $25
                  </div>
                </div>
              </>
            )}

            {/* Time Range */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                    {t('startTime')}:
                  </label>
                  <input
                    type="time"
                    value={specialForm.start_time}
                    onChange={(e) => setSpecialForm({ ...specialForm, start_time: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: theme?.borderRadius || 4,
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                    {t('endTime')}:
                  </label>
                  <input
                    type="time"
                    value={specialForm.end_time}
                    onChange={(e) => setSpecialForm({ ...specialForm, end_time: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: theme?.borderRadius || 4,
                      border: '1px solid #ddd',
                      fontSize: '1rem'
                    }}
                  />
                </div>
              </div>
              <div style={{
                fontSize: '0.75rem',
                color: theme?.colors?.textSecondary || '#666',
                fontStyle: 'italic',
                marginTop: '0.25rem'
              }}>
                {t('crossMidnightTimeNote')}
              </div>
            </div>

            {/* Days of Week */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('daysOfWeek')}:
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                {[0, 1, 2, 3, 4, 5, 6].map(day => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDayOfWeek(day)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: (specialForm.days_of_week || []).includes(day)
                        ? theme?.colors?.primary || '#1976d2'
                        : theme?.colors?.background || '#f5f5f5',
                      color: (specialForm.days_of_week || []).includes(day)
                        ? theme?.colors?.surface || '#fff'
                        : theme?.colors?.text || '#000',
                      border: '1px solid #ddd',
                      borderRadius: theme?.borderRadius || 4,
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {getDayName(day)}
                  </button>
                ))}
              </div>
            </div>

            {/* Priority */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: theme?.colors?.text || '#000' }}>
                {t('priority')}:
              </label>
              <input
                type="number"
                min="0"
                value={specialForm.priority}
                onChange={(e) => setSpecialForm({ ...specialForm, priority: parseInt(e.target.value) })}
                onWheel={(e) => e.target.blur()}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: '1px solid #ddd',
                  fontSize: '1rem'
                }}
              />
            </div>

            {/* Active Status */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.9rem',
                color: theme?.colors?.text || '#000',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={specialForm.active === 1}
                  onChange={(e) => setSpecialForm({ ...specialForm, active: e.target.checked ? 1 : 0 })}
                  style={{ marginRight: '0.5rem' }}
                />
                {t('active')}
              </label>
            </div>

            {/* Bottom Button Section */}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddSpecial(false);
                  setEditingSpecial(null);
                  resetForm();
                }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: theme?.colors?.error || '#f44336',
                  color: theme?.colors?.surface || '#fff',
                  border: 'none',
                  borderRadius: theme?.borderRadius || 4,
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold'
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleSaveSpecial}
                disabled={!specialForm.name}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: !specialForm.name ? '#ccc' : theme?.colors?.success || '#4caf50',
                  color: theme?.colors?.surface || '#fff',
                  border: 'none',
                  borderRadius: theme?.borderRadius || 4,
                  cursor: !specialForm.name ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  opacity: !specialForm.name ? 0.6 : 1
                }}
              >
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
